/**
 * Signing in to the web UI with Atlas (auth.repu.me): OpenID Connect
 * authorization code flow with PKCE for a public browser client. Tokens are
 * kept in this browser only; the refresh token rotates on every use.
 */
export interface AtlasConfig {
  issuer: string;
  webClientId: string;
}

export interface AtlasSession {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
  user: { uid: string; email: string | null; displayName: string | null };
}

const SESSION_KEY = "repu-ci-atlas-session";
const PENDING_KEY = "repu-ci-atlas-pending";
export const CALLBACK_PATH = "/auth/callback";

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function random(bytes = 32): string {
  return b64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

export async function challengeFor(verifier: string): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
}

export function decodeClaims(jwt: string): Record<string, unknown> {
  const part = jwt.split(".")[1] ?? "";
  return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
}

export function loadSession(storage: Storage = localStorage): AtlasSession | null {
  try {
    const raw = storage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AtlasSession) : null;
  } catch {
    return null;
  }
}

export function saveSession(session: AtlasSession | null, storage: Storage = localStorage): void {
  try {
    if (session) storage.setItem(SESSION_KEY, JSON.stringify(session));
    else storage.removeItem(SESSION_KEY);
  } catch {
    /* storage blocked: the session lasts for this page only */
  }
}

/** Starts sign-in: remembers the PKCE verifier, state and nonce, then goes to Atlas. */
export async function beginSignIn(config: AtlasConfig, returnTo: string): Promise<void> {
  const verifier = random(48);
  const state = random(16);
  const nonce = random(16);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ verifier, state, nonce, returnTo }));
  const params = new URLSearchParams({
    client_id: config.webClientId,
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    response_type: "code",
    scope: "openid email profile offline_access",
    state,
    nonce,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${config.issuer}/oidc/authorize?${params.toString()}`);
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
}

async function tokenRequest(config: AtlasConfig, body: Record<string, string>): Promise<TokenResponse> {
  const res = await fetch(`${config.issuer}/oidc/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: config.webClientId, ...body }).toString(),
  });
  const json = (await res.json().catch(() => ({}))) as Partial<TokenResponse> & { error?: string; error_description?: string };
  if (!res.ok || !json.access_token) throw new Error(json.error_description ?? json.error ?? `Sign-in failed (${res.status})`);
  return json as TokenResponse;
}

function toSession(tokens: TokenResponse, previous: AtlasSession | null): AtlasSession {
  const claims = decodeClaims(tokens.id_token ?? tokens.access_token);
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? previous?.refreshToken ?? null,
    expiresAt: Date.now() + tokens.expires_in * 1000,
    user: {
      uid: typeof claims["sub"] === "string" ? claims["sub"] : (previous?.user.uid ?? ""),
      email: typeof claims["email"] === "string" ? claims["email"] : (previous?.user.email ?? null),
      displayName: typeof claims["name"] === "string" ? claims["name"] : (previous?.user.displayName ?? null),
    },
  };
}

/** Finishes sign-in on the callback page. Returns the session and where to go next. */
export async function completeSignIn(config: AtlasConfig, search: string): Promise<{ session: AtlasSession; returnTo: string }> {
  const params = new URLSearchParams(search);
  const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? "null") as { verifier: string; state: string; nonce: string; returnTo: string } | null;
  sessionStorage.removeItem(PENDING_KEY);
  if (params.get("error")) throw new Error(params.get("error_description") ?? params.get("error") ?? "Sign-in was cancelled");
  if (!pending || params.get("state") !== pending.state) throw new Error("This sign-in link has expired. Sign in again.");
  if (params.get("iss") && params.get("iss") !== config.issuer) throw new Error("Sign-in response came from the wrong issuer");
  const tokens = await tokenRequest(config, {
    grant_type: "authorization_code",
    code: params.get("code") ?? "",
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    code_verifier: pending.verifier,
  });
  if (tokens.id_token && decodeClaims(tokens.id_token)["nonce"] !== pending.nonce) throw new Error("Sign-in response did not match this request");
  return { session: toSession(tokens, null), returnTo: pending.returnTo.startsWith("/") && !pending.returnTo.startsWith("//") ? pending.returnTo : "/" };
}

export async function refreshSession(config: AtlasConfig, session: AtlasSession): Promise<AtlasSession> {
  if (!session.refreshToken) throw new Error("Session expired");
  return toSession(await tokenRequest(config, { grant_type: "refresh_token", refresh_token: session.refreshToken }), session);
}

export function signOutUrl(config: AtlasConfig): string {
  return `${config.issuer}/oidc/logout?${new URLSearchParams({ client_id: config.webClientId, post_logout_redirect_uri: `${window.location.origin}/` }).toString()}`;
}
